import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MdQuiz, MdArrowBack, MdRateReview, MdEdit } from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import './Assessment.css';

const TYPE_LABELS = {
  single_select: 'Single Select',
  mcq: 'Multiple Select',
  essay: 'Essay / Writing',
  case_study: 'Case Study',
  text_input: 'Text',
};

const AssessmentDetail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [editable, setEditable] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/assessments/${id}?as=creator`);
        if (data.role !== 'creator') {
          toast.error('You do not have access to view this assessment');
          setAssessment(null);
          return;
        }
        setAssessment(data.data);
        setEditable(Boolean(data.editable));
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load assessment');
        setAssessment(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="assessment-page">
        <div className="assessment-loading">
          <span className="spinner" /> Loading…
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="assessment-page">
        <div className="assessment-empty">
          Assessment not found.{' '}
          <Link to="/assessments/created">Back</Link>
        </div>
      </div>
    );
  }

  const questions = [...(assessment.questions || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  return (
    <div className="assessment-result-page assessment-detail-page">
      <div className="assessment-result-hero">
        <div className="assessment-result-hero-inner">
          <div>
            <p className="assessment-result-kicker">Assessment details</p>
            <h1>
              <MdQuiz size={28} />
              {assessment.title}
            </h1>
            <p className="assessment-subtitle">
              {assessment.scheduledAt
                ? `${new Date(assessment.scheduledAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })} · `
                : ''}
              {assessment.durationMinutes} min · {assessment.totalMarks} marks ·{' '}
              {(assessment.assignedTo || []).length} assigned
              {assessment.createdBy?.name
                ? ` · Created by ${assessment.createdBy.name}`
                : ''}
            </p>
            {assessment.description ? (
              <p className="assessment-detail-desc">{assessment.description}</p>
            ) : null}
          </div>
          <div className="assessment-actions">
            <Link to="/assessments/created" className="assessment-btn secondary">
              <MdArrowBack size={18} />
              Back
            </Link>
            {editable && (
              <Link
                to={`/assessments/${assessment._id}/edit`}
                className="assessment-btn secondary"
              >
                <MdEdit size={18} />
                Edit
              </Link>
            )}
            <Link
              to={`/assessments/${assessment._id}/review`}
              className="assessment-btn"
            >
              <MdRateReview size={18} />
              Review attempts
            </Link>
          </div>
        </div>

        <div className="assessment-result-stats">
          <div>
            <span>Questions</span>
            <strong>{questions.length}</strong>
          </div>
          <div>
            <span>Total marks</span>
            <strong>{assessment.totalMarks}</strong>
          </div>
          <div>
            <span>Date &amp; time</span>
            <strong>
              {assessment.scheduledAt
                ? new Date(assessment.scheduledAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })
                : '—'}
            </strong>
          </div>
          <div>
            <span>Duration</span>
            <strong>{assessment.durationMinutes} min</strong>
          </div>
          <div>
            <span>Assignees</span>
            <strong>{(assessment.assignedTo || []).length}</strong>
          </div>
        </div>

        {(assessment.assignedTo || []).length > 0 && (
          <div className="assessment-detail-assignees">
            <span>Assigned to:</span>
            {(assessment.assignedTo || []).map((u) => (
              <em key={u._id || u}>{u.name || u}</em>
            ))}
          </div>
        )}
      </div>

      <div className="assessment-result-list">
        <h2>All questions ({questions.length})</h2>
        {questions.length === 0 ? (
          <div className="assessment-empty">No questions in this assessment.</div>
        ) : (
          questions.map((q, idx) => {
            const isObjective = q.type === 'single_select' || q.type === 'mcq';
            return (
              <article key={q._id} className="assessment-result-q">
                <div className="assessment-result-q-head">
                  <div>
                    <span className="assessment-exam-qnum">
                      Question {idx + 1}
                    </span>
                    <h3>{q.text}</h3>
                  </div>
                  <div className="assessment-result-q-badges">
                    <span className="assessment-exam-pill">
                      {TYPE_LABELS[q.type] || q.type}
                    </span>
                    <span className="assessment-exam-pill marks">
                      {q.marks} marks
                    </span>
                  </div>
                </div>

                {isObjective ? (
                  <div className="assessment-result-options">
                    {(q.options || []).map((o, oi) => {
                      const letter = String.fromCharCode(65 + oi);
                      return (
                        <div
                          key={o._id || oi}
                          className={`assessment-result-option ${
                            o.isCorrect ? 'is-correct' : ''
                          }`}
                        >
                          <span className="assessment-exam-letter">{letter}</span>
                          <span className="assessment-result-option-text">
                            {o.text}
                          </span>
                          <span className="assessment-result-option-tags">
                            {o.isCorrect && (
                              <em className="right">Correct answer</em>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="assessment-detail-manual-note">
                    Descriptive question — graded manually after staff submit.
                  </p>
                )}
              </article>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AssessmentDetail;
